import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppProvider } from './contexts/AppContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider, useWorkspace, WORKSPACE_PAGES } from './contexts/WorkspaceContext';
import { AlertsProvider } from './contexts/AlertsContext';
import { BubblePillNav, WorkspaceTabBar } from './components/BubblePillNav';
import { cn } from './lib/utils';

// i18n initialization
import './i18n';

// Pages
import Dashboard from './pages/Dashboard';
import Inbox from './pages/Inbox';
import Customers from './pages/Customers';
import CustomerBubbles from './pages/CustomerBubbles';
import Campaigns from './pages/Campaigns';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Products from './pages/Products';
import SocialBrowser from './pages/SocialBrowser';
import Login from './pages/Login';
import AdminUsers from './pages/AdminUsers';
import EscrowPayments from './pages/EscrowPayments';
import SubscriptionPlans from './pages/SubscriptionPlans';
import Invoices from './pages/Invoices';
import Profile from './pages/Profile';
import Onboarding from './pages/Onboarding';
import PrivacyPolicy, { TermsPage, DataDeletionPage } from './pages/Legal';
import PaymentMethods from './pages/PaymentMethods';
import BrunoDashboard from './pages/BrunoDashboard';
import SocialConnections from './pages/SocialConnections';
import DealsCenter from './pages/DealsCenter';
import Tickets from './pages/Tickets';
import Automations from './pages/Automations';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import { InstallPrompt } from './components/InstallPrompt';

import './App.css';
import { AppTour } from './components/AppTour';

const PAGE_COMPONENTS = {
  'dashboard': Dashboard,
  'pipeline': CustomerBubbles,
  'customers': Customers,
  'inbox': Inbox,
  'campaigns': Campaigns,
  'products': Products,
  'analytics': Analytics,
  'escrow': EscrowPayments,
  'invoices': Invoices,
  'social': SocialBrowser,
  'admin-users': AdminUsers,
  'plans': SubscriptionPlans,
  'settings': Settings,
  'profile': Profile,
  'payment-methods': PaymentMethods,
  'bruno': BrunoDashboard,
  'connections': SocialConnections,
  'deals': DealsCenter,
  'tickets': Tickets,
  'automations': Automations,
  'reports': Reports,
  'notifications': Notifications,
};

function BootSplashController() {
  const { loading } = useAuth();
  React.useEffect(() => {
    if (loading) return;
    const el = document.getElementById('deal-splash');
    if (!el) return;
    const t = setTimeout(() => {
      el.classList.add('ds-hide');
      setTimeout(() => el.remove(), 600);
    }, 500);
    return () => clearTimeout(t);
  }, [loading]);
  return null;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#002d2c]">
        <img src="/brand/splash-1.png" alt="Noah" className="w-32 animate-float" />
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Syncs workspace active tab with the URL - debounced to prevent flicker
function WorkspaceRouteSync() {
  const { activeTab, openWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialSync = React.useRef(true);

  // On initial mount: sync workspace to current URL (don't override URL with default tab)
  React.useEffect(() => {
    const page = WORKSPACE_PAGES.find(p => p.path === location.pathname);
    if (page && (!activeTab || activeTab.id !== page.id)) {
      openWorkspace(page.id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When user changes active tab: navigate to its path (skip first run to avoid clobbering initial URL)
  React.useEffect(() => {
    if (isInitialSync.current) {
      isInitialSync.current = false;
      return;
    }
    if (activeTab && activeTab.path && activeTab.path !== location.pathname) {
      navigate(activeTab.path, { replace: true });
    }
  }, [activeTab?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When URL changes (back/forward, direct nav): sync workspace
  React.useEffect(() => {
    const page = WORKSPACE_PAGES.find(p => p.path === location.pathname);
    if (page && (!activeTab || activeTab.id !== page.id)) {
      openWorkspace(page.id);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function AppLayout() {
  const { closeAllPanels } = useWorkspace();

  return (
    <div className="min-h-screen bg-background pb-12 sm:pb-20" onClick={(e) => {
      if (e.target.closest('[data-testid="bubble-pill-nav"]') ||
          e.target.closest('[data-testid^="panel-"]') ||
          e.target.closest('[data-testid="deal-menu"]')) return;
    }}>
      <WorkspaceRouteSync />
      <WorkspaceTabBar />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/bubbles" element={<CustomerBubbles />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/products" element={<Products />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/social" element={<SocialBrowser />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/escrow" element={<EscrowPayments />} />
          <Route path="/plans" element={<SubscriptionPlans />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/payment-methods" element={<PaymentMethods />} />
          <Route path="/bruno" element={<BrunoDashboard />} />
          <Route path="/connections" element={<SocialConnections />} />
          <Route path="/deals" element={<DealsCenter />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/automations" element={<Automations />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/notifications" element={<Notifications />} />
        </Routes>
      </main>
      <BubblePillNav />
      <InstallPrompt />
      <AppTour />
    </div>
  );
}

function AppRouter() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-[#002d2c]">
        <img src={`/brand/splash-${(parseInt(localStorage.getItem('deal_splash_idx') || '1', 10) % 2) + 1}.png`}
          alt="Noah" className="w-40 animate-float" />
        <div className="w-32 h-1 rounded-full bg-brand-mint/15 overflow-hidden">
          <div className="h-full w-1/3 rounded-full bg-brand-butter animate-slide-loop" />
        </div>
      </div>
    );
  }

  return (
    <>
      <BootSplashController />
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/onboarding" element={isAuthenticated ? <Onboarding /> : <Navigate to="/login" replace />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/terminos" element={<TermsPage />} />
      <Route path="/eliminar-datos" element={<DataDeletionPage />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <AppProvider>
            <WorkspaceProvider>
              <AlertsProvider>
                <AppLayout />
              </AlertsProvider>
            </WorkspaceProvider>
          </AppProvider>
        </ProtectedRoute>
      } />
    </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster 
            position="bottom-center" 
            offset="88px"
            mobileOffset="88px"
            richColors 
            closeButton
            toastOptions={{
              className: 'bg-card border-border'
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
